"use client";
import { Fragment } from "react";
import usePublicContacts, { phoneTel, phoneDisplay } from '@/hooks/usePublicContacts';
import Image from "next/image";
import Link from "next/link";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa6";
import SchoolERPAnimation from "./SchoolERPAnimation";
import FloatingSupport from "./FloatingSupport";
import { FOOTER_FEATURE_LINKS, TOTAL_FEATURES } from "./featureData";
import { useLanguage } from "@/contexts/LanguageContext";

/* Icons and destinations come from featureData — the same rows /features
   renders in its category rail — so this list cannot drift from the page it
   points at. The labels are looked up per language, keyed by the same group
   id, with featureData's English title as the fallback: a group added to
   FOOTER_FEATURE_IDS but not yet translated shows its English name rather
   than vanishing from the column. */
const featureLinks = FOOTER_FEATURE_LINKS;

/* Kayease Global — the company behind Kaympus. Same destination as the
   "About Us" row in the Company column and the © line's byline, so it is
   named once here rather than typed out at each use. */
const KAYEASE_URL = "https://kayease.com";

const companyLinks = [
  { key: "companyAbout", href: KAYEASE_URL, external: true },
  { key: "companyBlog", href: "/blog", external: false },
  { key: "companyPrivacy", href: "/privacy-policy", external: false },
  { key: "companyTerms", href: "/terms-and-conditions", external: false },
  { key: "companyRefund", href: "/refund-policy", external: false },
];

/* Kaympus has no handles of its own — these point at Kayease Global, the
   parent company, same as the "About Us" link in the Company column.
   No X entry: there is no confirmed handle, and a dead "#" link is worse
   than a missing icon — add it back here once the profile exists. */
const socials = [
  { icon: FaFacebookF, href: "https://www.facebook.com/kayeaseglobal/", label: "Kayease on Facebook" },
  { icon: FaInstagram, href: "https://www.instagram.com/kayease.global/", label: "Kayease on Instagram" },
  { icon: FaLinkedinIn, href: "https://www.linkedin.com/company/kayease", label: "Kayease on LinkedIn" },
];

/**
 * The © line with "Kayease" turned into a link to the parent company's site.
 *
 * It arrives as one translated sentence, so the byline can't be its own key
 * without forcing both languages into English word order — Hindi puts the
 * company before the postposition ("Kayease द्वारा"), English after the
 * preposition ("by Kayease"). Splitting the rendered string on the literal
 * brand name leaves each language's sentence exactly as written and only
 * changes what the name itself renders as.
 */
function Copyright({ text }: { text: string }) {
  const parts = text.split(/(Kayease)/g);

  return (
    <>
      {parts.map((part, i) =>
        part === "Kayease" ? (
          <a
            key={i}
            href={KAYEASE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-slate-700 underline decoration-[#B9C8F6] underline-offset-2 hover:text-[#3D5EE1] hover:decoration-[#3D5EE1] transition-colors duration-200 rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5EE1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#EFF3FE]"
          >
            {part}
          </a>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        )
      )}
    </>
  );
}

/**
 * Footer — three stacked registers, tallest element on the page:
 *
 *   1. Link grid    — brand region (campus scene, then logo / blurb /
 *                     socials beside it) + Features / Company / Contact, all
 *                     text columns starting on one baseline. On lg+ the
 *                     scene is self-end and nudged down 4% of its own height
 *                     so the road inside the SVG lands ON the bottom-bar
 *                     divider — the hairline reads as the road's horizon and
 *                     the bus drives along it.
 *   2. Ground band  — mobile only (< lg): the same scene, centered and
 *                     seated on the divider the same way.
 *   3. Bottom bar   — © line and legal links under the divider.
 */
export default function Footer() {
  const contacts = usePublicContacts();
  const { t, tf, tv } = useLanguage();

  /* Shared with /features — same map, same ids, so the six rows here can't
     drift from the seventeen the catalogue renders. One lookup for the whole
     map rather than one per row, so a language missing the block degrades to
     featureData's English titles instead of six raw dot-paths. */
  const featureCopy = tv<Record<string, { title: string; tagline: string }>>(
    "features.groups"
  );

  return (
    <footer className="w-full bg-[#EEF1FC] text-slate-800 relative overflow-hidden font-sans border-t border-[#DCE1FA]">

      {/* ── Minimal background geometry ──────────────────────────────────
          Masked so the pattern never runs under the link columns — the
          footer is dense with small text and an unmasked lattice behind it
          reads as noise. */}

      {/* Dot lattice, fading in from the bottom edge only — it gathers as
          "air" around the campus scene in the ground band. */}
      <div
        aria-hidden
        className="absolute inset-0 lp-dots opacity-40 pointer-events-none
                   [mask-image:linear-gradient(to_top,black,transparent_58%)]"
        style={{ backgroundSize: "32px 32px" }}
      />

      {/* Rings anchored off the top-right corner, so only the outer arcs
          sweep behind the Contact column's whitespace — kept away from the
          bottom-right where the campus scene now lives. */}
      <div
        aria-hidden
        className="hidden md:block absolute top-[-12rem] right-[-16rem] w-[580px] h-[580px]
                   rounded-full lp-rings opacity-60 pointer-events-none
                   [mask-image:radial-gradient(circle,transparent_45%,black_62%,transparent_88%)]"
      />

      {/* Sparse dot wedge tucked into the top-left corner */}
      <div
        aria-hidden
        className="absolute inset-0 lp-dots opacity-40 pointer-events-none
                   [mask-image:linear-gradient(125deg,black,transparent_26%)]"
        style={{ backgroundSize: "36px 36px" }}
      />

      {/* Background Soft Lighting & Accent Glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#3D5EE1]/[0.06] rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-500/[0.05] rounded-full blur-[120px] pointer-events-none" />

      {/* Full-bleed: no max-width cap, only the site's edge padding — so the
          footer content runs edge to edge instead of sitting inside the 1720px
          content column. */}
      <div className="w-full px-4 sm:px-6 lg:px-10 pt-8 sm:pt-9 lg:pt-10 relative z-10">

        {/* ── Register 1: link grid ─────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-10 xl:gap-x-14">

          {/* Brand region — campus scene on the left, brand stack beside it.
              The grid row stretches to the tallest column, and the scene is
              self-end + nudged down 4% of its own height, so its road strip
              lands exactly on the bottom-bar divider two registers down: the
              hairline reads as the road's horizon and the bus drives along
              it. Desktop-only here; below lg the scene renders centered in
              its own ground band above the divider instead. */}
          <div className="lg:col-span-5 flex items-start gap-4 xl:gap-6">
            <SchoolERPAnimation
              className="hidden xl:block w-[220px] 2xl:w-[260px] shrink-0 aspect-[4/3] overflow-hidden
                         self-end translate-y-[4%] pointer-events-none select-none"
            />

            <div className="flex flex-col items-start gap-4 min-w-0 flex-1">
              <Link href="/" className="relative w-[190px] sm:w-[250px] h-11 sm:h-[52px] block notranslate" translate="no" aria-label="Kaympus Home">
                {/* Neither `priority` nor a 100vw srcset: the footer is the
                    bottom of every page, and `priority` was putting a preload
                    for this logo in the <head> ahead of the render-blocking
                    CSS on every marketing page. `sizes` mirrors the box
                    (w-[190px] sm:w-[250px]) the same way the header logo
                    does, so the browser picks a ~500px candidate for a
                    250px logo instead of the 100vw default. */}
                <Image
                  src="/logo.png"
                  alt="Kaympus"
                  fill
                  sizes="(max-width: 640px) 190px, 250px"
                  className="object-contain object-left"
                />
              </Link>

              <p className="text-[13px] sm:text-[13.5px] text-slate-600 leading-relaxed font-medium max-w-[42ch] text-balance">
                {t("footer.blurb")}
              </p>

              <div className="flex items-center gap-3 pt-0.5">
                {socials.map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    className="w-9 h-9 rounded-full bg-white hover:bg-[#3D5EE1] border border-[#D6E0FC] hover:border-[#3D5EE1] text-[#3D5EE1] hover:text-white flex items-center justify-center transition-all duration-200 shadow-xs hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5EE1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#EFF3FE]"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Link columns — share the remaining width equally so all three
              headings start on the same baseline as the logo. */}
          {/* pb keeps the last link row ("All 76+ Features") clear of the
              bottom-bar divider; the campus scene beside it is self-end +
              translated so it alone touches the hairline. */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-x-8 lg:gap-x-12 xl:gap-x-16 gap-y-8 pb-6 lg:pb-10">

            {/* Features */}
            <div>
              {/* h2, not h3. The footer is the last landmark on every page, and on
                  pages whose body has no h2 of its own (the contact page, for
                  one) an h3 here jumped straight from the page h1 to level 3.
                  Heading level is how a screen-reader user navigates a page
                  structurally, so a skipped level reads as a missing section.
                  These column titles ARE top-level divisions of the footer.
                  Going back "up" to h2 after deeper headings elsewhere on a
                  page is valid; only skipping down a level is not. Size and
                  weight come from the classes, so nothing moves. */}
              <h2 className="font-bold text-[12px] sm:text-[13px] text-[#0d1117] tracking-widest uppercase flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-[#3D5EE1] shrink-0" />
                {t("footer.headingFeatures")}
              </h2>
              <ul className="space-y-2.5">
                {featureLinks.map((item) => {
                  const Icon = item.icon;
                  const copy = featureCopy?.[item.id];
                  return (
                    <li key={item.id}>
                      {/* scroll={false}: the hash names a category in the
                          catalogue's tablist, not an element — left to itself
                          the router would find no such id and scroll to the top
                          of the page. FeatureCatalogue reads the hash, opens
                          the right tab and scrolls to the section itself. */}
                      <Link
                        href={item.href}
                        title={copy?.tagline ?? item.tagline}
                        scroll={false}
                        className="group inline-flex items-center gap-2.5 text-[13px] sm:text-[13.5px] text-slate-700 hover:text-[#3D5EE1] font-medium transition-all duration-200"
                      >
                        <div className="w-5 h-5 rounded-md bg-[#3D5EE1]/10 text-[#3D5EE1] flex items-center justify-center shrink-0 group-hover:bg-[#3D5EE1] group-hover:text-white transition-colors duration-200">
                          <Icon size={12} strokeWidth={2.5} />
                        </div>
                        <span className="group-hover:translate-x-0.5 transition-transform duration-200 leading-tight">
                          {copy?.title ?? item.label}
                        </span>
                      </Link>
                    </li>
                  );
                })}
                <li className="pt-1">
                  <Link
                    href="/features"
                    className="group inline-flex items-center gap-2 text-[13px] sm:text-[13.5px] text-[#3D5EE1] hover:text-[#2A48C9] font-bold transition-all duration-200"
                  >
                    <span>{tf("footer.allFeatures", { count: TOTAL_FEATURES })}</span>
                    <ArrowRight
                      size={13}
                      className="shrink-0 transition-transform duration-200 group-hover:translate-x-1"
                      aria-hidden
                    />
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              {/* h2, not h3. The footer is the last landmark on every page, and on
                  pages whose body has no h2 of its own (the contact page, for
                  one) an h3 here jumped straight from the page h1 to level 3.
                  Heading level is how a screen-reader user navigates a page
                  structurally, so a skipped level reads as a missing section.
                  These column titles ARE top-level divisions of the footer.
                  Going back "up" to h2 after deeper headings elsewhere on a
                  page is valid; only skipping down a level is not. Size and
                  weight come from the classes, so nothing moves. */}
              <h2 className="font-bold text-[12px] sm:text-[13px] text-[#0d1117] tracking-widest uppercase flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-[#3D5EE1] shrink-0" />
                {t("footer.headingCompany")}
              </h2>
              <ul className="space-y-2">
                {companyLinks.map((item) => (
                  <li key={item.key}>
                    <Link
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      className="inline-flex items-center text-[13px] sm:text-[13.5px] text-slate-600 hover:text-[#3D5EE1] font-medium transition-colors duration-200"
                    >
                      {t(`footer.${item.key}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="col-span-2 sm:col-span-1">
              {/* h2, not h3. The footer is the last landmark on every page, and on
                  pages whose body has no h2 of its own (the contact page, for
                  one) an h3 here jumped straight from the page h1 to level 3.
                  Heading level is how a screen-reader user navigates a page
                  structurally, so a skipped level reads as a missing section.
                  These column titles ARE top-level divisions of the footer.
                  Going back "up" to h2 after deeper headings elsewhere on a
                  page is valid; only skipping down a level is not. Size and
                  weight come from the classes, so nothing moves. */}
              <h2 className="font-bold text-[12px] sm:text-[13px] text-[#0d1117] tracking-widest uppercase flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-[#3D5EE1] shrink-0" />
                {t("footer.headingContact")}
              </h2>
              <ul className="space-y-2.5">
                <li>
                  <a
                    href={`tel:${phoneTel(contacts.supportPhone)}`}
                    className="inline-flex items-center gap-2.5 text-slate-700 hover:text-[#3D5EE1] transition-colors duration-200 group"
                  >
                    <div className="w-7 h-7 rounded-full bg-white border border-[#D6E0FC] text-[#3D5EE1] flex items-center justify-center shrink-0 shadow-xs group-hover:border-[#3D5EE1]">
                      <Phone size={12} />
                    </div>
                    <span className="text-[13px] sm:text-[13.5px] font-semibold truncate">{phoneDisplay(contacts.supportPhone)}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${contacts.salesEmail}`}
                    className="inline-flex items-center gap-2.5 text-slate-700 hover:text-[#3D5EE1] transition-colors duration-200 group"
                  >
                    <div className="w-7 h-7 rounded-full bg-white border border-[#D6E0FC] text-[#3D5EE1] flex items-center justify-center shrink-0 shadow-xs group-hover:border-[#3D5EE1]">
                      <Mail size={12} />
                    </div>
                    <span className="text-[13px] sm:text-[13.5px] font-semibold truncate">{contacts.salesEmail}</span>
                  </a>
                </li>
              </ul>

              <Link
                href="/contact"
                className="group inline-flex items-center gap-2 mt-4 rounded-full bg-[#3D5EE1] hover:bg-[#2A48C9] px-5 py-2.5 text-[12px] sm:text-[12.5px] font-bold tracking-wide text-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5EE1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#EFF3FE]"
              >
                {t("footer.bookDemo")}
                <ArrowRight size={13} className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
              </Link>
            </div>

          </div>{/* end link columns */}

        </div>{/* end register 1 */}

        {/* ── Register 2: mobile ground band ────────────────────────────
            Below lg the brand region hides the scene, so it renders here
            instead — centered, seated on the divider the same way
            (translate-y-[4%] puts the SVG's road strip on the hairline). */}
        <div className="relative flex items-end justify-center mt-1 sm:mt-2 lg:hidden">
          <SchoolERPAnimation
            className="w-[270px] min-[420px]:w-[310px] sm:w-[360px] shrink-0 aspect-[4/3]
                       overflow-hidden translate-y-[4%] pointer-events-none select-none"
          />
        </div>

        {/* Register 3: bottom bar — ultra slim and compact */}
        <div className="pt-5 pb-7 border-t border-[#D6E0FC] flex flex-col sm:flex-row items-center justify-between gap-3 font-medium lg:pr-20">
          <p className="text-slate-600 text-[12px] sm:text-[12.5px] text-center sm:text-left">
            <Copyright text={t("footer.copyright")} />
          </p>

          <nav className="flex items-center gap-4 sm:gap-6 text-[12px] sm:text-[12.5px] text-slate-600">
            <Link href="/support" className="py-2 hover:text-[#3D5EE1] transition-colors duration-200">{t("footer.bottomSupport")}</Link>
            <Link href="/privacy-policy" className="py-2 hover:text-[#3D5EE1] transition-colors duration-200">{t("footer.bottomPrivacy")}</Link>
            <Link href="/terms-and-conditions" className="py-2 hover:text-[#3D5EE1] transition-colors duration-200">{t("footer.bottomTerms")}</Link>
            <Link href="/refund-policy" className="py-2 hover:text-[#3D5EE1] transition-colors duration-200">{t("footer.bottomRefund")}</Link>
          </nav>
        </div>

      </div>

      {/* Floating WhatsApp + Support buttons — mounted here so every page
          that renders the marketing chrome gets them, and only those pages.
          `fixed` positioning means the DOM location inside the footer does
          not affect where they appear (bottom-right of the viewport). */}
      <FloatingSupport />
    </footer>
  );
}

